import { useState } from 'react';
import {
  UNIT_TYPES, unitTypeLabel, formatMoney,
  type HebergementProperty, type HebergementUnit, type UnitType,
} from '@/lib/hebergement';

interface Props {
  properties: HebergementProperty[];
  units: HebergementUnit[];
  currency: string;
  selectedPropertyId: number | null;
  onSelectProperty: (id: number | null) => void;
  createUnit: (data: Partial<HebergementUnit>) => Promise<HebergementUnit>;
  updateUnit: (id: number, patch: Partial<HebergementUnit>) => Promise<void>;
  deleteUnit: (id: number) => Promise<void>;
}

interface Draft {
  id: number | null;
  name: string;
  type: UnitType;
  description: string;
  capacity: string;
  beds: string;
  bathrooms: string;
  price: string;
  published: boolean;
}

const emptyDraft: Draft = {
  id: null, name: '', type: 'room', description: '', capacity: '2', beds: '1', bathrooms: '1', price: '0', published: false,
};

export default function UnitsView({
  properties, units, currency, selectedPropertyId, onSelectProperty, createUnit, updateUnit, deleteUnit,
}: Props) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState<HebergementUnit | null>(null);

  const propertyId = selectedPropertyId ?? properties[0]?.id ?? null;
  const property = properties.find((p) => p.id === propertyId) || null;
  const propertyUnits = units.filter((u) => u.property_id === propertyId);

  const openCreate = () => { setDraft({ ...emptyDraft }); setError(''); };
  const openEdit = (u: HebergementUnit) => {
    setDraft({
      id: u.id, name: u.name, type: u.type, description: u.description || '',
      capacity: String(u.capacity), beds: String(u.beds), bathrooms: String(u.bathrooms),
      price: String(u.price), published: u.published,
    });
    setError('');
  };

  const save = async () => {
    if (!draft || !propertyId) return;
    if (!draft.name.trim()) { setError('Le nom de l\u2019unité est obligatoire.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload: Partial<HebergementUnit> = {
        property_id: propertyId,
        name: draft.name.trim(),
        type: draft.type,
        description: draft.description.trim() || null,
        capacity: Math.max(1, parseInt(draft.capacity, 10) || 1),
        beds: Math.max(1, parseInt(draft.beds, 10) || 1),
        bathrooms: Math.max(1, parseInt(draft.bathrooms, 10) || 1),
        price: parseFloat(draft.price) || 0,
        published: draft.published,
        status: draft.published ? 'active' : 'draft',
      };
      if (draft.id) await updateUnit(draft.id, payload);
      else await createUnit(payload);
      setDraft(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de l\u2019enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    await deleteUnit(deleting.id);
    setDeleting(null);
  };

  if (properties.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-dashed border-background-300/60 rounded-lg text-center">
        <div className="w-16 h-16 rounded-2xl bg-secondary-100 text-secondary-600 flex items-center justify-center mb-4">
          <i className="ri-door-open-line text-3xl"></i>
        </div>
        <h3 className="text-base font-semibold text-foreground-800 mb-1">Créez d&apos;abord un bien</h3>
        <p className="text-sm text-foreground-500 max-w-sm">Les chambres et unités se rattachent à un hébergement existant.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-semibold text-foreground-600 whitespace-nowrap">Bien :</label>
          <select
            value={propertyId ?? ''}
            onChange={(e) => onSelectProperty(e.target.value ? Number(e.target.value) : null)}
            className="px-3 py-2 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-950 focus:outline-none focus:border-primary-300 cursor-pointer max-w-[240px]"
          >
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-add-line"></i>Ajouter une unité
        </button>
      </div>

      {property && property.booking_mode === 'ENTIRE_PROPERTY' && propertyUnits.length === 0 && (
        <div className="px-4 py-3 rounded-lg bg-amber-50 border border-amber-200/70 flex items-start gap-2.5">
          <i className="ri-information-line text-amber-600 mt-0.5"></i>
          <p className="text-xs text-amber-800">
            « {property.name} » est configuré pour se louer <strong>en entier</strong>. Vous pouvez tout de même ajouter des unités
            (utile pour le calendrier), mais pensez à passer le mode de réservation sur « Les deux » dans la fiche du bien.
          </p>
        </div>
      )}

      {propertyUnits.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-dashed border-background-300/60 rounded-lg text-center">
          <i className="ri-door-line text-3xl text-foreground-300 mb-3"></i>
          <p className="text-sm text-foreground-500">Aucune unité pour « {property?.name} »</p>
          <p className="text-xs text-foreground-400 mt-1">Optionnel : ajoutez des chambres si le bien se loue chambre par chambre.</p>
        </div>
      ) : (
        <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-5 py-3 bg-background-100 border-b border-background-200/70 text-xs font-semibold text-foreground-500 uppercase tracking-wider">
            <div className="col-span-4">Unité</div>
            <div className="col-span-2">Capacité</div>
            <div className="col-span-2">Prix / nuit</div>
            <div className="col-span-2">Statut</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          <div className="divide-y divide-background-200/70">
            {propertyUnits.map((u) => (
              <div key={u.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-5 py-3.5 items-center">
                <div className="md:col-span-4 flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center flex-shrink-0">
                    <i className="ri-hotel-bed-line"></i>
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground-900 truncate">{u.name}</div>
                    <div className="text-xs text-foreground-400">{unitTypeLabel(u.type)}</div>
                  </div>
                </div>
                <div className="md:col-span-2 text-sm text-foreground-600">{u.capacity} pers.</div>
                <div className="md:col-span-2 text-sm font-medium text-foreground-900">{formatMoney(Number(u.price) || 0, currency)}</div>
                <div className="md:col-span-2">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    u.published ? 'bg-accent-100 text-accent-700' : 'bg-background-100 text-foreground-500'
                  }`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${u.published ? 'bg-accent-500' : 'bg-foreground-300'}`}></span>
                    {u.published ? 'Publiée' : 'Brouillon'}
                  </span>
                </div>
                <div className="md:col-span-2 flex items-center justify-end gap-1">
                  <button onClick={() => openEdit(u)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-background-100 text-foreground-500 transition-colors cursor-pointer" title="Modifier">
                    <i className="ri-edit-line text-sm"></i>
                  </button>
                  <button onClick={() => setDeleting(u)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-red-50 text-foreground-400 hover:text-red-500 transition-colors cursor-pointer" title="Supprimer">
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {draft && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDraft(null)}></div>
          <div className="relative w-full sm:max-w-lg bg-background-50 sm:rounded-2xl rounded-t-2xl border border-background-200/70 max-h-[92vh] flex flex-col">
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
              <h3 className="text-sm font-bold text-foreground-950">{draft.id ? 'Modifier l\u2019unité' : 'Nouvelle unité'}</h3>
              <button onClick={() => setDraft(null)} className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer">
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {error && (
                <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100">
                  <span className="text-xs text-red-700">{error}</span>
                </div>
              )}
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Nom *</label>
                <input type="text" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="Ex. Chambre 1" className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Type</label>
                <select value={draft.type} onChange={(e) => setDraft({ ...draft, type: e.target.value as UnitType })}
                  className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300 cursor-pointer">
                  {UNIT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Capacité</label>
                  <input type="number" min={1} value={draft.capacity} onChange={(e) => setDraft({ ...draft, capacity: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Lits</label>
                  <input type="number" min={1} value={draft.beds} onChange={(e) => setDraft({ ...draft, beds: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">SDB</label>
                  <input type="number" min={1} value={draft.bathrooms} onChange={(e) => setDraft({ ...draft, bathrooms: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-foreground-600 mb-1.5">Prix ({currency})</label>
                  <input type="number" min={0} value={draft.price} onChange={(e) => setDraft({ ...draft, price: e.target.value })}
                    className="w-full px-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm focus:outline-none focus:border-primary-300" />
                </div>
              </div>
              <button type="button" onClick={() => setDraft({ ...draft, published: !draft.published })}
                className="w-full flex items-center justify-between p-3.5 rounded-lg border border-background-200/70 bg-background-50 cursor-pointer">
                <span className="flex items-center gap-2 text-sm text-foreground-800">
                  <i className={draft.published ? 'ri-global-line text-accent-600' : 'ri-eye-off-line text-foreground-400'}></i>
                  Publier cette unité
                </span>
                <span className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${draft.published ? 'bg-accent-500' : 'bg-background-200'}`}>
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${draft.published ? 'translate-x-6' : 'translate-x-1'}`} />
                </span>
              </button>
            </div>
            <div className="flex items-center justify-end gap-3 px-5 h-16 border-t border-background-200/70 flex-shrink-0">
              <button onClick={() => setDraft(null)} className="px-4 py-2.5 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={save} disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary-500 text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50">
                {saving ? <><i className="ri-loader-4-line animate-spin"></i>Enregistrement…</> : <><i className="ri-check-line"></i>{draft.id ? 'Enregistrer' : 'Créer'}</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleting && (
        <>
          <div className="fixed inset-0 bg-black/40 z-[70]" onClick={() => setDeleting(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[70] w-full max-w-sm bg-background-50 rounded-2xl border border-background-200/70 p-6">
            <h3 className="text-sm font-semibold text-foreground-950 mb-4">Supprimer « {deleting.name} » ?</h3>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap">Annuler</button>
              <button onClick={confirmDelete} className="px-5 py-2 rounded-full text-sm font-semibold bg-red-500 text-white whitespace-nowrap hover:bg-red-600 transition-colors cursor-pointer">Supprimer</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}